import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useMethod, useUser } from '/imports/hooks';
import { getProductionIntervals, pullProductionIntervals } from '/imports/api/productionIntervals';
import { MILLIS_24_HOURS } from '/imports/constants';
import { LoadingFill } from '/imports/ui/LoadingFill';
import { Bar, BarChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { chartColors, formatDate, formatDateTime, formatNumber } from '/imports/utils';
import { ProductionMeter } from '/imports/api/productionMeters';
import { strings } from '/imports/strings';
import { DateTimeInput } from '/imports/ui/DateTimeInput';
import { Button } from '/imports/ui/Button';
import { toastOnError } from '/imports/ui/utils';

type ProductionIntervalsVsTimeChartProps = {
  meter: ProductionMeter;
  // from: Date;
  // to: Date;
};

const ProductionIntervalsVsTimeChartStyled = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 20rem;
  .wrapper {
    height: 100%;
    min-height: 20rem;
    overflow: hidden;
  }
  .stats {
    display: flex;
    gap: 2rem;
    justify-content: center;
  }
  .range-picker {
    position: absolute;
    top: -2.5rem;
    right: 1rem;
    display: flex;
    gap: 0.5rem;
    span {
      margin-right: 0.5rem;
    }
    .clear {
      right: 0.75rem;
    }
  }
  .overlay {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0.5rem;
    left: 0;
    background-color: white;
    z-index: 111;
  }
`;

/** Bar chart of a production meter's solar output over a selectable time range. */
export const ProductionIntervalsVsTimeChart = (props: ProductionIntervalsVsTimeChartProps) => {
  const { meter } = props;
  const meterId = meter._id;
  const zone = meter.timezone;
  const user = useUser();
  const format = user?.dateTimeFormat;
  const [initializing, setInitializing] = useState(true);
  const [from, setFrom] = useState<Date>(new Date(Date.now() - 7 * MILLIS_24_HOURS));
  const [to, setTo] = useState<Date>(new Date());
  const [reload, setReload] = useState(Math.random());
  const [series, intervalsLoading] = useMethod(getProductionIntervals, { meterId, from, to }, [reload]);
  const rows = series?.intervals;
  const totalSolarProduction = useMemo(() => (rows || []).reduce((t, i) => t + i.production, 0), [rows]);
  useEffect(() => { if (initializing && !intervalsLoading) setInitializing(false); }, [intervalsLoading]);
  const pullProduction = toastOnError(async () => {
    const interval = setInterval(() => setReload(Math.random()), 2500);
    const toastId = toast.info(strings.Running(), { autoClose: MILLIS_24_HOURS });
    await pullProductionIntervals.call({ meterIds: [meterId], from, to, sync: true });
    clearInterval(interval);
    setReload(Math.random());
    toast.dismiss(toastId);
  });
  // The server picks the bucket width from the range, so a year draws twelve bars instead of 35,000. The
  // label says which, because a daily bar and a 15-minute bar look the same on an unlabelled axis.
  const granularity = series?.granularity;
  const resolutionLabel = granularity === 'monthly' ? strings.ChartResolutionMonthly()
    : granularity === 'daily' ? strings.ChartResolutionDaily()
      : granularity === 'hourly' ? strings.ChartResolutionHourly() : strings.ChartResolutionFifteenMin();
  const formatBucket = (timestamp: number) => (granularity === 'monthly' || granularity === 'daily'
    ? formatDate(new Date(timestamp), format, zone)
    : formatDateTime(new Date(timestamp), format, zone));
  return (
    <ProductionIntervalsVsTimeChartStyled className="production-intervals-vs-time-chart">
      {
        initializing ?
          <LoadingFill />
          :
          <div className="wrapper" >
            <ResponsiveContainer width="100%" height="100%">
              {
                intervalsLoading ?
                  <div className="overlay"><LoadingFill /></div>
                  :
                  <BarChart
                    data={rows}
                    margin={{
                      // top: 5,
                      // right: 30,
                      // left: 40,
                      // bottom: 5
                    }}
                  >
                    {
                      rows?.length ?
                        <XAxis type="number" dataKey="startTime" name="timestamp" domain={['dataMin', 'dataMax']} tickFormatter={(item) => formatBucket(item)} />
                        :
                        <XAxis type="number" dataKey="startTime" name="timestamp" domain={['dataMin', 'dataMax']} />
                    }
                    <YAxis unit="kWh" />
                    <ReferenceLine y={0} stroke="#000" />
                    {/* <Tooltip labelFormatter={(timestamp) => formatDateTime(new Date(timestamp))} wrapperStyle={{ background: css.white, zIndex: 100000, pointerEvents: 'auto', position: 'fixed' }} /> */}
                    <Tooltip labelFormatter={(timestamp) => formatBucket(timestamp)} formatter={(value) => `${formatNumber(parseFloat((value || '0') as string), 1)}`} />
                    {/* <Legend payload={[
                    { value: strings.SolarExport(), color: chartColors[0], type: 'rect' },
                    { value: strings.SolarUsage(), color: chartColors[2], type: 'rect' },
                    { value: strings.FromGrid(), color: chartColors[3], type: 'rect' }
                  ]} /> */}
                    <Bar dataKey="production" barSize={20} name={strings.Production()} fill={chartColors[0]} unit="kWh" isAnimationActive={false} />
                    {/* <Bar dataKey="productionNotUsed" stackId="a" barSize={20} name={strings.SolarExport()} fill={chartColors[0]} unit="kWh" />
                  <Bar dataKey="totalFromGrid" stackId="a" barSize={20} name={`${strings.UsageFromGrid()}: ${strings.Total()}`} fill={chartColors[3]} unit="kWh" height={0} /> */}
                    {/* {consumptionMeters.map((m) =>
                    <Bar key={`${m._id}fromgrid`} dataKey={`${m._id}_fromGrid`} stackId="a" barSize={20} name={`${strings.UsageFromGrid()}: ${m.name}`} fill={chartColors[3]} unit="kWh" />
                  )} */}
                  </BarChart>
              }
            </ResponsiveContainer>
            <div className="range-picker">
              <div>
                <span>{strings.From()}</span><DateTimeInput value={from} onChange={(from) => from && setFrom(from)} zone={zone} />
              </div>
              <div>
                <span>{strings.To()}</span><DateTimeInput value={to} onChange={(to) => to && setTo(to)} zone={zone} />
              </div>
              <Button onClick={() => setReload(Math.random())}>{strings.Go()}</Button>
              {user?.isSuperUser ? <Button onClick={() => pullProduction()}>{strings.Pull()}</Button> : null}
            </div>
            <div className="stats">
              <span>{strings.SolarProduction()}: {formatNumber(totalSolarProduction)} kWh</span>
              <span>{strings.ChartResolution()}: {resolutionLabel}</span>
            </div>
          </div>
      }
    </ProductionIntervalsVsTimeChartStyled>
  );
};